import {Edit} from "@mui/icons-material";
import {Stack} from "@mui/material";
import React, {useContext, useState} from "react";
import {RelationshipDto} from "../../../../../common/apis/relationship/RelationshipDto";
import {ArchimateRelationship} from "../../../../../common/archimate/ArchimateRelationship";
import ElementPropertyDefinitionContext from "../../../../../common/ElementPropertyDefinitionContext";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import Grid from "../../../../../components/dialogs/Grid";
import TextField from "../../../../../components/fields/textfield/TextField";
import SectionActionBar from "../../../../../components/section/SectionActionBar";
import {SectionActionButton} from "../../../../../components/section/SectionActionButton";
import SectionHeader from "../../../../../components/section/SectionHeader";
import SectionTitle from "../../../../../components/section/SectionTitle";
import {PersistentStateId} from "../../../../../store/common/Grid";
import {_transl} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import PropertiesPanel, {PropertyApi} from "../../property/PropertiesPanel";
import {PropertiesTranslationKey} from "../../property/PropertiesTranslationKey";
import {RelationshipsTranslationKey} from "../RelationshipsTranslationKey";
import ConnectableConceptInfoPanel from "./ConnectableConceptDetailPanel";
import {RelationshipPropertyWrapperApi} from "./RelationshipPropertyWrapperApi";
import {RelationshipUpdateDialog} from "./RelationshipUpdateDialog";


export interface RelationshipDetailDialogProps {
    open: boolean;
    onClose: () => void;
    relationship: RelationshipDto;
    onRelationshipUpdated?: () => void;
}

export default function RelationshipDetailDialog(props: Readonly<RelationshipDetailDialogProps>) {
    const {open, onClose, relationship, onRelationshipUpdated} = props;

    const propertyDefinitionContext = useContext(ElementPropertyDefinitionContext);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

    const propertyClientApi: PropertyApi = React.useMemo(() => {
        return new RelationshipPropertyWrapperApi(relationship.identifier, propertyDefinitionContext);
    }, [relationship.identifier, propertyDefinitionContext]);

    return (
            <>
                <Dialog open={open}
                        onClose={onClose}
                        maxWidth={"lg"}>
                    <DialogTitle title={_transl(RelationshipsTranslationKey.RELATIONSHIP_DETAIL)} onDialogClosed={onClose}/>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <SectionHeader title={_transl(RelationshipsTranslationKey.DETAILS)}>
                                    <SectionActionBar>
                                        <SectionActionButton
                                                icon={<Edit fontSize={"inherit"}/>}
                                                tooltip={_transl(CommonTranslationKey.EDIT)}
                                                onClick={() => setUpdateDialogOpen(true)}
                                                visible={relationship.acl.canUpdate}/>
                                    </SectionActionBar>
                                </SectionHeader>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField label={_transl(RelationshipsTranslationKey.TYPE)}
                                        value={ArchimateRelationship.findByStandardName(relationship.type)?.visibleName ?? relationship.type}
                                        InputProps={{
                                            readOnly: true,
                                        }}/>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <TextField label={_transl(RelationshipsTranslationKey.NAME)}
                                        value={relationship.name ?? ""}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label={_transl(CommonTranslationKey.DESCRIPTION)}
                                        value={relationship.description ?? ""}
                                        readOnly={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <ConnectableConceptInfoPanel title={_transl(RelationshipsTranslationKey.SOURCE)}
                                        connectableConcept={relationship.source}/>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <ConnectableConceptInfoPanel title={_transl(RelationshipsTranslationKey.TARGET)}
                                        connectableConcept={relationship.target}/>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack spacing={2} mt={0}>
                                    <SectionTitle>{_transl(PropertiesTranslationKey.PROPERTIES)}</SectionTitle>
                                    <PropertiesPanel
                                            canUpdateProperties={relationship.acl.canUpdate}
                                            propertyApi={propertyClientApi}
                                            persistentStateId={PersistentStateId.RELATIONSHIP_DETAIL_PROPERTIES_GRID}
                                            resourceId={relationship.identifier}/>
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </Dialog>

                <RelationshipUpdateDialog
                        open={updateDialogOpen}
                        relationship={relationship}
                        onClose={() => setUpdateDialogOpen(false)}
                        onUpdated={() => onRelationshipUpdated?.()}/>
            </>
    );
}
